import React, { useId, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Image from '@plone/volto/components/theme/Image/Image';
import { caminhoDaPagina, useImagensDaPagina } from './busca';
import {
  alternar,
  casar,
  chaveDaImagem,
  idDaImagem,
  legendaDe,
  legendar,
  marcarTodas,
  tirarSumidas,
} from './escolha';
import {
  AVISO_CARREGANDO,
  AVISO_ERRO,
  AVISO_SEM_PAGINA,
  AVISO_VAZIO,
  notaDasSumidas,
} from './View';

export const AJUDA_DA_ESCOLHA =
  'Marque as fotos que entram na galeria. Legenda em branco usa a descrição da imagem.';
const SEM_DESCRICAO = 'Escreva a legenda desta foto';

// Na barra lateral, no modo de escolha: as imagens da página, cada uma com
// caixa de marcar e, se marcada, o campo da legenda. Lê a busca que a galeria
// já fez (mesma chave) e só grava quando o editor mexe: o formulário do Volto
// regrava o bloco ao abrir, e uma gravação daqui no mesmo instante se perderia.
const EscolhaDasFotos = ({
  data,
  block,
  onChangeBlock,
  properties,
  metadata,
}) => {
  const base = useId();
  const caminho = caminhoDaPagina({ properties, metadata });
  const { busca, imagens } = useImagensDaPagina({
    bloco: block,
    caminho,
    buscar: false,
  });
  const { escolhidas } = data;
  const { casadas, sumidas } = useMemo(
    () => casar(imagens, escolhidas),
    [imagens, escolhidas],
  );
  // Legenda de quem foi desmarcado nesta edição: volta se a foto for marcada
  // de novo (um clique errado não apaga o que foi escrito).
  const guardadas = useRef(new Map());
  // A contagem (ou o aviso) recebe o foco quando o botão clicado some.
  const alvoDoFoco = useRef(null);
  const gravar = (lista) =>
    onChangeBlock(block, { ...data, escolhidas: lista });

  const alternarFoto = (imagem, entrada) => {
    const chave = chaveDaImagem(imagem);
    if (entrada) guardadas.current.set(chave, legendaDe(entrada));
    gravar(alternar(imagens, escolhidas, imagem, guardadas.current.get(chave)));
  };

  let aviso = null;
  if (caminho === null) aviso = AVISO_SEM_PAGINA;
  else if (busca?.error) aviso = AVISO_ERRO;
  else if (!busca?.loaded) aviso = AVISO_CARREGANDO;
  else if (imagens.length === 0) aviso = AVISO_VAZIO;
  // Antes de a busca voltar, toda escolha pareceria ter saído da página.
  const pronta = caminho !== null && busca?.loaded && !busca?.error;

  const marcadas = casadas.size;
  const todasMarcadas = marcadas === imagens.length;
  return (
    <div className="ui attached segment ag-galeria-escolha">
      <p className="ag-galeria-escolha__ajuda">{AJUDA_DA_ESCOLHA}</p>
      {pronta && sumidas > 0 && (
        // A legenda de uma foto apagada continua no bloco, que vai inteiro
        // para quem lê a página, até sair da lista.
        <div className="ag-galeria-escolha__sumidas">
          <p className="ag-galeria-escolha__nota">{notaDasSumidas(sumidas)}</p>
          <button
            type="button"
            className="ag-galeria-escolha__botao"
            onClick={() => {
              gravar(tirarSumidas(imagens, escolhidas));
              if (alvoDoFoco.current) alvoDoFoco.current.focus();
            }}
          >
            Tirar da lista
          </button>
        </div>
      )}
      {aviso ? (
        <p className="ag-galeria-escolha__aviso" ref={alvoDoFoco} tabIndex={-1}>
          {aviso}
        </p>
      ) : (
        <>
          <div className="ag-galeria-escolha__topo">
            <p
              className="ag-galeria-escolha__contagem"
              aria-live="polite"
              ref={alvoDoFoco}
              tabIndex={-1}
            >
              {`Escolhidas: ${marcadas} de ${imagens.length}`}
            </p>
            {/* Sempre presente: se sumisse com o clique, o foco do teclado
                cairia no começo da página. Sem "Desmarcar todas": um clique
                não pode apagar as legendas. */}
            <button
              type="button"
              className="ag-galeria-escolha__botao"
              aria-disabled={todasMarcadas ? 'true' : undefined}
              onClick={() => {
                if (!todasMarcadas)
                  gravar(marcarTodas(imagens, escolhidas, guardadas.current));
              }}
            >
              Marcar todas
            </button>
          </div>
          <ul className="ag-galeria-escolha__lista">
            {imagens.map((imagem, i) => {
              const entrada = casadas.get(chaveDaImagem(imagem));
              const nome = imagem.title || idDaImagem(imagem);
              const idLegenda = `${base}-legenda-${i}`;
              return (
                <li key={imagem['@id']} className="ag-galeria-escolha__item">
                  <label className="ag-galeria-escolha__marcar">
                    <input
                      type="checkbox"
                      className="ag-galeria-escolha__caixa"
                      checked={Boolean(entrada)}
                      onChange={() => alternarFoto(imagem, entrada)}
                    />
                    <Image
                      item={imagem}
                      alt=""
                      loading="lazy"
                      sizes="4rem"
                      className="ag-galeria-escolha__mini"
                    />
                    <span className="ag-galeria-escolha__nome">{nome}</span>
                  </label>
                  {entrada && (
                    <div className="field ag-galeria-escolha__legenda">
                      <label htmlFor={idLegenda}>
                        Legenda
                        <span className="ag-galeria__so-leitor">{` de ${nome}`}</span>
                      </label>
                      <textarea
                        id={idLegenda}
                        rows={2}
                        value={legendaDe(entrada)}
                        placeholder={imagem.description || SEM_DESCRICAO}
                        onChange={(e) =>
                          gravar(
                            legendar(
                              imagens,
                              escolhidas,
                              imagem,
                              e.target.value,
                            ),
                          )
                        }
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

EscolhaDasFotos.propTypes = {
  data: PropTypes.object.isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  properties: PropTypes.shape({ '@id': PropTypes.string }),
  metadata: PropTypes.shape({ '@id': PropTypes.string }),
};

export default EscolhaDasFotos;
